import React, { useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { useFela, ThemeContext } from 'react-fela'
import { isEmpty, isEqual } from 'lodash'

import DropDownMaterial from 'design-framework/components/DropDownMaterial/index'

import { __wrapper } from './__style'

const MinMaxSelect = ({ range, values, options, onChange, disabled, step }) => {
	const { css } = useFela()
	const theme = useContext(ThemeContext)
	const [value, setValue] = useState({
		min: values?.min || range.min,
		max: values?.max || range.max,
	})

	const { min: minValue, max: maxValue } = value

	useEffect(() => {
		if (!isEmpty(values) && !isEqual(values, value)) {
			setValue(values)
		}
	}, [values])

	let optionList = [range.min, range.max]

	options.forEach(
		opt =>
			opt >= range.min + step &&
			opt <= range.max - step &&
			optionList.push(opt)
	)
	optionList = optionList
		.sort((a, b) => a - b)
		.reduce((agg, a) => [...agg, { key: a, value: a }], [])

	const [minRangeOptions, setMinRangeOptions] = useState(
		optionList.filter(opt => opt.key < maxValue)
	)
	const [maxRangeOptions, setMaxRangeOptions] = useState(
		optionList.filter(opt => opt.key > minValue)
	)

	useEffect(() => {
		setMinRangeOptions(optionList.filter(opt => opt.key < maxValue))
		setMaxRangeOptions(optionList.filter(opt => opt.key > minValue))
	}, [minValue, maxValue])

	const handleMinValueChange = key => {
		if (key < maxValue) {
			setValue({ min: key, max: maxValue })
			onChange({ min: key, max: maxValue })
		}
	}
	const handleMaxValueChange = key => {
		if (key > minValue) {
			setValue({ min: minValue, max: key })
			onChange({ min: minValue, max: key })
		}
	}

	const renderValue = val => {
		const renderedValue =
			typeof val === 'number'
				? `₹ ${String(val).replace(
						/\B(?=(\d{2})*(\d{3})(?!\d))/g,
						','
				  )}`
				: val

		return <div>{renderedValue}</div>
	}

	return (
		<div className={css(__wrapper)}>
			<DropDownMaterial
				payload={minRangeOptions}
				variant="outlined"
				minWidth="90px"
				margin="dense"
				unsetWrapperPadding
				showMenuAtBottom
				disabled={disabled}
				value={minValue}
				handleChange={handleMinValueChange}
				renderValue={renderValue}
				themeToUse={theme}
			/>
			<DropDownMaterial
				payload={maxRangeOptions}
				variant="outlined"
				minWidth="90px"
				margin="dense"
				unsetWrapperPadding
				showMenuAtBottom
				disabled={disabled}
				value={maxValue}
				handleChange={handleMaxValueChange}
				renderValue={renderValue}
				themeToUse={theme}
			/>
		</div>
	)
}

MinMaxSelect.defaultProps = {
	values: {},
	range: { min: 0, max: 1000 },
	options: [0, 500, 1000, 2000],
	onChange: () => {},
	disabled: false,
	step: 1,
}

MinMaxSelect.propTypes = {
	/** Min and Max Selected Values of the slider  */
	values: PropTypes.shape({
		min: PropTypes.number,
		max: PropTypes.number,
	}),
	/** Min and Max Range of the slider */
	range: PropTypes.shape({
		min: PropTypes.number,
		max: PropTypes.number,
	}),
	/** Options values to be shown */
	options: PropTypes.arrayOf(PropTypes.number),
	/** function to execute on value change */
	onChange: PropTypes.func,
	/** Boolean to disable the dropdown */
	disabled: PropTypes.bool,
	/** Enable steps to fix the Range */
	step: PropTypes.number,
}

export default MinMaxSelect
